import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO, useTicker } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, LifeProgressBar, WeekdayMilestoneGrid, buildAppFaqLd, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "Milestone Birthday Calculator: 18, 21, 30, 40, 50, 60 Countdown Cards";
const DESC = "Interactive milestone birthday countdown — see live days, hours and weekdays for turning 18, 21, 30, 40, 50, 60, 75 and 100 with beautiful, shareable cards.";

const FAQS = [
  { q: "Which birthdays are considered milestones?", a: "Culturally: 18 (adulthood), 21 (legal drinking in US), 30 (career milestone), 40 (mid-life), 50 (Golden Jubilee), 60 (Diamond / retirement), 75 (Platinum), 100 (Centenarian)." },
  { q: "How many days until my 30th birthday?", a: "Enter your DOB above and the tool will show the exact days, hours and weekday of the exact date you turn 30 — plus every other milestone in one glance." },
  { q: "Can I plan a surprise party using this?", a: "Yes. The card shows the exact weekday so you can pick the closest Friday or Saturday for the event." },
  { q: "What does 'Sweet 16' or 'Quinceañera' fall under?", a: "Add them as custom entries — the calculator supports any positive age. Culturally, they map to the 16th and 15th birthdays respectively." },
];

const TOC = [
  { id: "calculator", label: "Countdown Cards" },
  { id: "cultural", label: "Cultural Milestones" },
  { id: "planning", label: "Party Planning" },
  { id: "faqs", label: "FAQs" },
];

const DEFAULT_MS = [18, 21, 25, 30, 40, 50, 60, 75, 100];

export const Route = createFileRoute("/age-calculator/milestone-birthday")({
  head: () => leafHead({
    path: "/age-calculator/milestone-birthday", title: TITLE, description: DESC,
    scripts: [
      buildAppFaqLd({ name: "Milestone Birthday Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "Milestone Birthday", url: "https://drhint.com/age-calculator/milestone-birthday" },
      ]),
    ],
  }),
  component: Page,
});

function Page() {
  const [dob, setDob] = useState("");
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  useTicker(revealed);

  const cards = useMemo(() => {
    if (!dob) return [];
    const d = new Date(dob);
    if (isNaN(d.getTime())) return [];
    const now = new Date();
    return DEFAULT_MS.map((age) => {
      const target = new Date(d);
      target.setFullYear(d.getFullYear() + age);
      const ms = target.getTime() - now.getTime();
      const past = ms <= 0;
      const abs = Math.abs(ms);
      const days = Math.floor(abs / 86400000);
      const hours = Math.floor((abs % 86400000) / 3600000);
      const weekday = target.toLocaleDateString("en-US", { weekday: "long" });
      const dateStr = target.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      return { age, past, days, hours, weekday, dateStr };
    });
  }, [dob, revealed]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyText = cards.length ? cards.filter((c) => !c.past).slice(0, 3).map((c) => `${c.age}: ${c.days}d on ${c.weekday}`).join(" | ") : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Milestone Birthday
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Har landmark birthday ka live-ticking countdown — <strong>18, 21, 30, 40, 50, 60, 75, 100</strong> — plus exact weekday jab wo din aayega. Party plan karo ya nostalgia — sab ek screen par. Standard age <Link to="/age-calculator" className="text-primary underline">yahan</Link>.
        </p>
      </section>
      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <TripleDateInput label="Your Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Show Milestone Cards" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={320}>
            {revealed && cards.length ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {cards.map((c) => (
                    <div key={c.age} className={`rounded-2xl border p-5 transition ${c.past ? "border-border bg-muted/40" : "border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card"}`}>
                      <div className="flex items-baseline justify-between">
                        <div className="text-3xl font-black text-primary">{c.age}</div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.past ? "Completed" : "Upcoming"}</div>
                      </div>
                      <div className="mt-2 text-sm font-semibold">{c.dateStr}</div>
                      <div className="text-xs text-muted-foreground">{c.weekday}</div>
                      <div className="mt-3 rounded-lg bg-background/70 border border-border px-3 py-2 text-sm tabular-nums">
                        {c.past ? `🎉 ${c.days.toLocaleString()} days ago` : `⏳ ${c.days.toLocaleString()} days · ${c.hours}h left`}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-center"><CopyResultButton text={copyText} label="Copy Next 3 Milestones" /></div>
                <LifeProgressBar dob={dob} />
                <WeekdayMilestoneGrid dob={dob} />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter your DOB to unlock all 9 milestone cards.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="cultural" title="What Each Milestone Traditionally Marks">
        <ul>
          <li><strong>18:</strong> Legal adulthood, voting, driving licence in most jurisdictions.</li>
          <li><strong>21:</strong> Legal drinking (US), full inheritance access in many trusts.</li>
          <li><strong>30:</strong> "Third Decade" — career, marriage or property benchmarks.</li>
          <li><strong>40:</strong> Traditional mid-life reset — health screenings recommended.</li>
          <li><strong>50:</strong> Golden Jubilee, US AARP eligibility.</li>
          <li><strong>60:</strong> Retirement in India / Pakistan Central Govt; NPS window opens.</li>
          <li><strong>75 & 100:</strong> Platinum & Centenarian — Queen's / Prime Minister's card in UK & India.</li>
        </ul>
      </AnchorSection>

      <AnchorSection id="planning" title="Turn a Card Into a Party Plan">
        <p>Tap a milestone that lands on a <em>Tuesday</em>? Shift the celebration to the closest weekend the same week — the calculator's weekday output is the fastest way to pick a Saturday that guests can actually attend without leave requests. Booking a venue 6+ months out for milestones ending in 0 or 5 is standard advice from professional planners.</p>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}